import type {
  RoutePlanCoverage, CoverageSlot, CoverageExtraVisit, CoverageDoubleEntry,
} from '@/api/route-plans';

// Value 0 is the "Sin categoría" bucket, same convention as the board's pending filter.
export const UNCATEGORIZED_FILTER_VALUE = 0;

export type CoverageColumnKey = 'missing' | 'inProcess' | 'covered';

export const COLUMN_ORDER: CoverageColumnKey[] = ['missing', 'inProcess', 'covered'];

export type CoverageWarning =
  | { kind: 'timeDrift' }
  | { kind: 'caregiverDrift'; name: string }
  | { kind: 'rejected' }
  | { kind: 'scheduleBlock'; description: string }
  | { kind: 'doubleCoverage'; name: string; start: string; end: string };

export interface CoverageCard {
  key: string;
  column: CoverageColumnKey;
  patientId: number;
  patientFullName: string;
  categoryId: number | null;
  categoryName: string | null;
  categoryPriority: number | null;
  fixedStartTime: string | null;
  timeWindowStart: string | null;
  timeWindowEnd: string | null;
  durationMinutes: number | null;
  skillName: string | null;
  preferredCaregiverName: string | null;
  caregiverName: string | null;
  coverageStartTime: string | null;
  coverageEndTime: string | null;
  fromExistingShift: boolean;
  blocked: boolean;
  isExtra: boolean;
  slot: CoverageSlot | null;
  warnings: CoverageWarning[];
}

export interface CoverageBand {
  categoryId: number | null;
  categoryName: string | null;
  categoryPriority: number | null;
  columns: Record<CoverageColumnKey, CoverageCard[]>;
  total: number;
}

// Timeless requirements ("any time that day") become an all-day window: the visit model
// requires a window unless a fixed time is given.
export const ALL_DAY_WINDOW = { start: '00:00', end: '23:59' };

export function attrsFromSlot(slot: CoverageSlot) {
  const requirement = slot.requirement;
  const windowed = !requirement.fixedStartTime;

  return {
    patientId: slot.patientId,
    durationMinutes: requirement.durationMinutes,
    skillId: requirement.skillId,
    assignedCaregiverId: requirement.preferredCaregiverId,
    fixedStartTime: requirement.fixedStartTime ?? undefined,
    timeWindowStart: windowed ? (requirement.timeWindowStart ?? ALL_DAY_WINDOW.start) : undefined,
    timeWindowEnd: windowed ? (requirement.timeWindowEnd ?? ALL_DAY_WINDOW.end) : undefined,
  };
}

// Blocked slots stay in "Falta cubrir": still expected, just not offered for quick-add.
function columnForSlotState(state: CoverageSlot['state']): CoverageColumnKey {
  if (state === 'missing' || state === 'blocked') return 'missing';
  if (state === 'pending') return 'inProcess';

  return 'covered';
}

// Keyed by route_plan_visit_id so the duplicate marker follows the colliding assignment
// wherever it lands: on its slot card, or on an extra card when a shift won the slot.
function doubleByVisitId(coverage: RoutePlanCoverage): Map<number, CoverageDoubleEntry> {
  const map = new Map<number, CoverageDoubleEntry>();
  for (const entry of coverage.doubleCoverage) map.set(entry.routePlanVisitId, entry);

  return map;
}

function doubleWarning(
  doubles: Map<number, CoverageDoubleEntry>,
  routePlanVisitId: number | null | undefined,
): CoverageWarning[] {
  const entry = routePlanVisitId ? doubles.get(routePlanVisitId) : undefined;
  if (!entry) return [];

  return [{
    kind: 'doubleCoverage',
    name: entry.shiftCaregiverName ?? '',
    start: entry.shiftStartTime ?? '',
    end: entry.shiftEndTime ?? '',
  }];
}

function driftWarnings(slot: CoverageSlot): CoverageWarning[] {
  const warnings: CoverageWarning[] = [];
  if (slot.timeDrift) warnings.push({ kind: 'timeDrift' });
  if (slot.caregiverDrift) {
    warnings.push({ kind: 'caregiverDrift', name: slot.requirement.preferredCaregiverName ?? '' });
  }

  return warnings;
}

function rejectionWarning(slot: CoverageSlot): CoverageWarning[] {
  return slot.coverage?.assignmentStatus === 'rejected' ? [{ kind: 'rejected' }] : [];
}

// Any slot carrying a block gets the warning — covered ones too, so a shift or assignment
// inside a block is flagged like in the calendars.
function scheduleBlockWarning(slot: CoverageSlot): CoverageWarning[] {
  return slot.scheduleBlock ? [{ kind: 'scheduleBlock', description: slot.scheduleBlock.description }] : [];
}

function slotWarnings(slot: CoverageSlot, doubles: Map<number, CoverageDoubleEntry>): CoverageWarning[] {
  return [
    ...scheduleBlockWarning(slot),
    ...driftWarnings(slot),
    ...rejectionWarning(slot),
    ...doubleWarning(doubles, slot.coverage?.routePlanVisitId),
  ];
}

function slotCoverageFields(slot: CoverageSlot) {
  const slotCoverage = slot.coverage;
  if (!slotCoverage) return { caregiverName: null, coverageStartTime: null, coverageEndTime: null };

  return {
    caregiverName: slotCoverage.caregiverName,
    coverageStartTime: slotCoverage.startTime,
    coverageEndTime: slotCoverage.endTime,
  };
}

function cardFromSlot(slot: CoverageSlot, doubles: Map<number, CoverageDoubleEntry>): CoverageCard {
  return {
    key: `slot-${slot.weeklyPlanId}`,
    column: columnForSlotState(slot.state),
    patientId: slot.patientId,
    patientFullName: slot.patientFullName,
    categoryId: slot.serviceCategoryId,
    categoryName: slot.serviceCategoryName,
    categoryPriority: slot.serviceCategoryPriority,
    fixedStartTime: slot.requirement.fixedStartTime,
    timeWindowStart: slot.requirement.timeWindowStart,
    timeWindowEnd: slot.requirement.timeWindowEnd,
    durationMinutes: slot.requirement.durationMinutes,
    skillName: slot.requirement.skillName,
    preferredCaregiverName: slot.requirement.preferredCaregiverName,
    ...slotCoverageFields(slot),
    fromExistingShift: slot.state === 'existing_shift',
    blocked: slot.state === 'blocked',
    isExtra: false,
    slot,
    warnings: slotWarnings(slot, doubles),
  };
}

// Extras keep their "Extra" chip but flow like any visit: placed ones count as covered,
// unplaced ones sit in "En proceso" — nobody is serving them yet.
function cardFromExtra(
  extra: CoverageExtraVisit,
  index: number,
  doubles: Map<number, CoverageDoubleEntry>,
): CoverageCard {
  return {
    key: `extra-${extra.routePlanVisitId ?? index}`,
    column: extra.state === 'assigned' ? 'covered' : 'inProcess',
    patientId: extra.patientId,
    patientFullName: extra.patientFullName,
    categoryId: extra.serviceCategoryId,
    categoryName: extra.serviceCategoryName,
    categoryPriority: extra.serviceCategoryPriority,
    fixedStartTime: extra.fixedStartTime,
    timeWindowStart: extra.timeWindowStart,
    timeWindowEnd: extra.timeWindowEnd,
    durationMinutes: extra.durationMinutes,
    skillName: null,
    preferredCaregiverName: null,
    caregiverName: extra.caregiverName,
    coverageStartTime: extra.startTime,
    coverageEndTime: null,
    fromExistingShift: false,
    blocked: false,
    isExtra: true,
    slot: null,
    warnings: doubleWarning(doubles, extra.routePlanVisitId),
  };
}

export function buildCards(coverage: RoutePlanCoverage): CoverageCard[] {
  const doubles = doubleByVisitId(coverage);

  return [
    ...coverage.slots.map((slot) => cardFromSlot(slot, doubles)),
    ...coverage.extraVisits.map((extra, index) => cardFromExtra(extra, index, doubles)),
  ];
}

function bandPriority(band: CoverageBand): number {
  return band.categoryPriority ?? -1;
}

function bandName(band: CoverageBand): string {
  return band.categoryName ?? '';
}

// Highest category priority first (mirrors the optimizer's ordering), name as tie-break,
// uncategorized always last.
function bandOrder(a: CoverageBand, b: CoverageBand): number {
  if (a.categoryId === null) return 1;
  if (b.categoryId === null) return -1;
  const byPriority = bandPriority(b) - bandPriority(a);

  return byPriority === 0 ? bandName(a).localeCompare(bandName(b)) : byPriority;
}

function bandFor(bands: Map<number, CoverageBand>, card: CoverageCard): CoverageBand {
  const bandKey = card.categoryId ?? UNCATEGORIZED_FILTER_VALUE;
  let band = bands.get(bandKey);
  if (!band) {
    band = {
      categoryId: card.categoryId,
      categoryName: card.categoryName,
      categoryPriority: card.categoryPriority,
      columns: { missing: [], inProcess: [], covered: [] },
      total: 0,
    };
    bands.set(bandKey, band);
  }

  return band;
}

export function groupCards(cards: CoverageCard[]): CoverageBand[] {
  const bands = new Map<number, CoverageBand>();
  for (const card of cards) {
    const band = bandFor(bands, card);
    band.columns[card.column].push(card);
    band.total += 1;
  }
  for (const band of bands.values()) {
    // Stable sort keeps the backend's within-patient time order.
    for (const column of COLUMN_ORDER) {
      band.columns[column].sort((a, b) => a.patientFullName.localeCompare(b.patientFullName));
    }
  }

  return [...bands.values()].sort(bandOrder);
}

export function filterBands(bands: CoverageBand[], selectedIds: number[]): CoverageBand[] {
  if (selectedIds.length === 0) return bands;

  return bands.filter((band) => selectedIds.includes(band.categoryId ?? UNCATEGORIZED_FILTER_VALUE));
}

export function hasCategories(cards: CoverageCard[]): boolean {
  return cards.some((card) => card.categoryId !== null);
}
